import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, Check, ChevronDown, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * Company picker with a text filter.
 *
 * A plain select becomes unusable once the list is long enough that you have to
 * remember where a name sits. Matches on both display name and id, since the id
 * is what appears on orders and invoices.
 */
export default function CompanySearchSelect({ companies, value, onChange, placeholder = 'Search companies…' }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const boxRef = useRef(null);

    useEffect(() => {
        const onClickAway = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickAway);
        return () => document.removeEventListener('mousedown', onClickAway);
    }, []);

    const selected = companies.find(c => c.companyId === value);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return companies;
        return companies.filter(c =>
            String(c.companyName || '').toLowerCase().includes(q) ||
            String(c.companyId || '').toLowerCase().includes(q));
    }, [companies, query]);

    return (
        <div ref={boxRef} className="relative">
            <button
                type="button"
                onClick={() => { setOpen(o => !o); setQuery(''); }}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-left text-sm"
            >
                <span className={selected ? '' : 'text-muted-foreground'}>
                    {selected ? `${selected.companyName} (${selected.companyId})` : placeholder}
                </span>
                <span className="flex items-center gap-1">
                    {selected && (
                        <X
                            className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); onChange(''); }}
                        />
                    )}
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </span>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
                    <div className="relative border-b border-border p-2">
                        <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            autoFocus
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Type a name or id…"
                            className="h-8 pl-7 text-sm"
                        />
                    </div>

                    <div className="max-h-60 overflow-y-auto py-1">
                        {matches.length === 0 && (
                            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                                Nothing matches “{query}”.
                            </p>
                        )}
                        {matches.map(c => (
                            <button
                                key={c.companyId}
                                type="button"
                                onClick={() => { onChange(c.companyId); setOpen(false); }}
                                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted ${
                                    c.companyId === value ? 'bg-muted/60' : ''
                                }`}
                            >
                                <span className="min-w-0">
                                    <span className="block truncate font-medium">{c.companyName}</span>
                                    <span className="block truncate font-mono text-[11px] text-muted-foreground">
                                        {c.companyId}
                                    </span>
                                </span>
                                {c.companyId === value && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
